import { computed } from "vue";
import store from "../store";
import { KhkAdminService } from "../services/KhkAdminService";

/**
 * RBAC capabilities (issue #1878).
 *
 * Capabilities are the single source of truth for what the admin UI may show.
 * They are fetched once from GET /admin/me/capabilities (the backend resolves
 * them from the caller's Okta groups) and cached in Vuex — never derived
 * client-side from groups, so the client can't drift from the backend matrix.
 */

// Module-level promise dedupes concurrent loads (e.g. several router navigations
// firing before the first fetch resolves).
let loadPromise = null;

/**
 * Ensure the current admin's capabilities are loaded into the store.
 * Idempotent: returns the cached set unless `force` is set (use force right
 * after login, since a previous admin's set may be persisted in localStorage).
 * Never throws — on failure it caches an empty set (deny-by-default).
 * @param {boolean} force refetch even if already cached
 * @returns {Promise<string[]>}
 */
export async function ensureCapabilitiesLoaded(force = false) {
    if (force) {
        loadPromise = null;
    }
    if (!force && store.state.capabilities && store.state.capabilities.length > 0) {
        return store.state.capabilities;
    }
    if (!loadPromise) {
        const service = new KhkAdminService();
        loadPromise = service
            .fetchMyCapabilities()
            .then(caps => {
                store.commit("setCapabilities", caps);
                return caps;
            })
            .catch(error => {
                console.error("Failed to load admin capabilities", error);
                store.commit("setCapabilities", []);
                loadPromise = null; // allow a later retry
                return [];
            });
    }
    return loadPromise;
}

/**
 * Synchronous capability check against the cached set. Used outside component
 * context (router guard) and inside reactive predicates (reads store.state, so
 * Vue tracks it as a dependency).
 * @param {string} capability
 * @returns {boolean}
 */
export function hasCapability(capability) {
    return store.state.capabilities?.includes(capability) ?? false;
}

/**
 * Composable form for use in components.
 */
export function useCapabilities() {
    const capabilities = computed(() => store.state.capabilities ?? []);
    return {
        capabilities,
        hasCapability,
        ensureCapabilitiesLoaded
    };
}

export default useCapabilities;
